import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { PORTAL_PASSWORD, PORTAL_USERS } from '@config/auth';
import { AUTH_SESSION_KEY } from '@config/storageKeys';
import type { AuthContextValue, AuthUser } from '@type/auth';
import type { ModuleId } from '@type/module';

const AuthContext = createContext<AuthContextValue | null>(null);

function restoreUser(email: string | null | undefined): AuthUser | null {
  const key = String(email || '').trim().toLowerCase();
  const profile = PORTAL_USERS[key];
  if (!profile) return null;
  return { email: key, ...profile };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);

  useEffect(() => {
    try {
      const savedEmail = sessionStorage.getItem(AUTH_SESSION_KEY);
      if (savedEmail) {
        const restored = restoreUser(savedEmail);
        if (restored) setUser(restored);
        else sessionStorage.removeItem(AUTH_SESSION_KEY);
      }
    } catch {
      /* ignore */
    } finally {
      setIsRestoring(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const key = String(email || '').trim().toLowerCase();
    const profile = PORTAL_USERS[key];
    if (!profile || password !== PORTAL_PASSWORD) return null;
    setIsSigningIn(true);
    await new Promise((r) => setTimeout(r, 850));
    const nextUser: AuthUser = { email: key, ...profile };
    setUser(nextUser);
    try {
      sessionStorage.setItem(AUTH_SESSION_KEY, key);
    } catch {
      /* ignore */
    }
    setIsSigningIn(false);
    return nextUser;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    try {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const canAccess = useCallback(
    (moduleId: ModuleId) => {
      if (!user) return false;
      return user.modules.includes(moduleId);
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isSigningIn,
      isRestoring,
      login,
      logout,
      canAccess,
      isAuthenticated: !!user,
    }),
    [user, isSigningIn, isRestoring, login, logout, canAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
